import { useSearchParams } from "react-router";
import { Collapse } from "@/components/design/Collapse";
import { AccessInspectionPanel } from "./AccessInspectionPanel";
import { List, Users } from "lucide-react";
import { useIntl } from "react-intl";
import { QUICK_ACTION_CLASS } from "@/components/dashboard/StatTile";

export function AccessManagementPanel({ onOpenRoles, onOpenGroups }: {
  onOpenRoles: () => void;
  onOpenGroups: (action?: "create" | "members" | "roles") => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [params, setParams] = useSearchParams();
  const inspecting = params.get("accessShortcut") === "inspection" || params.get("section") === "accessInspection";
  function toggleInspection() {
    const next = new URLSearchParams(params);
    next.set("section", "accessManagement");
    if (inspecting) next.delete("accessShortcut"); else next.set("accessShortcut", "inspection");
    setParams(next, { replace: true, preventScrollReset: true });
  }
  const cards = [
    { key: "roles", icon: List, title: "admin.roles.title", actions: [
      { id: "access-inspect-person", label: "admin.inspection.title", run: toggleInspection },
      { id: "access-openroles", label: "admin.access.openRoles", run: onOpenRoles },
      { id: "access-assign-role", label: "admin.access.workflow.assign", run: () => onOpenGroups("roles") },
    ] },
    { key: "groups", icon: Users, title: "admin.access.shortcuts.personGroups", actions: [
      { id: "access-create-group", label: "admin.groups.create", run: () => onOpenGroups("create") },
      { id: "access-manage-members", label: "admin.access.workflow.members", run: () => onOpenGroups("members") },
    ] },
  ];
  return <section aria-labelledby="access-management-title" className="@container space-y-6">
    <div>
      <h2 id="access-management-title" className="text-smycles-title font-medium">{t("settings.sectionAccessManagement")}</h2>
      <p className="mt-2 max-w-3xl text-smycles-body text-ink">{t("admin.access.workflow.intro")}</p>
    </div>
    <section aria-labelledby="access-shortcuts-title" className="space-y-4">
      <h3 id="access-shortcuts-title" className="smycles-field-label">{t("admin.access.shortcuts.title")}</h3>
      <div className="grid gap-6 @min-[40rem]:grid-cols-2">
        {cards.map(card => <section key={card.key} aria-label={t(card.title)} className="box min-w-0 bg-surface p-5">
          <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-ink/[0.06]" aria-hidden="true">
            <card.icon size={24} strokeWidth={1.5} />
          </div>
          <h4 className="mb-3 text-smycles-body font-medium">{t(card.title)}</h4>
          <div className="space-y-1">{card.actions.map(action => <button type="button" key={action.id} id={action.id}
            aria-expanded={action.id === "access-inspect-person" ? inspecting : undefined}
            aria-controls={action.id === "access-inspect-person" ? "access-inspection" : undefined}
            onClick={action.run} className={`${QUICK_ACTION_CLASS} w-full ${action.id === "access-inspect-person" && inspecting ? "bg-ink/[0.06]" : ""} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui`}>
            <span aria-hidden="true" className="text-ink-muted">+</span><span>{t(action.label)}</span>
          </button>)}</div>
        </section>)}
      </div>
    </section>
    <Collapse open={inspecting}><AccessInspectionPanel /></Collapse>
    <p className="max-w-3xl text-smycles-meta text-ink-muted">{t("admin.access.workflow.sharing")}</p>
  </section>;
}
